import { Spinner, Tabs, TTab, UIHeading3, UIHeading4 } from '@cian/ui-kit';
import * as React from 'react';

import * as styles from './TabFolderMenu.css';
import { ERoute, TRoute } from '../../types/route';
import { ETopMenuStatus } from '../../types/topMenuStatus';

type TTabFolderMenuProps = {
  currentRoute: TRoute;
  onChangeRoute(route: TRoute): void;
  status: ETopMenuStatus;
  isMobile: boolean;
  isMasterTabsEnabled: boolean;
  changeListButtonNode: React.ReactNode | null;
};

const DEFAULT_TITLE = 'Сообщения';

const MenuItems: { [key in ERoute]: string } = {
  [ERoute.Messages]: 'Активные',
  [ERoute.Unread]: 'Непрочитанные',
  [ERoute.Archive]: 'Архив',
  [ERoute.Spam]: 'Заблокированные',
};

export const TabFolderMenu: React.FC<TTabFolderMenuProps> = ({
  currentRoute,
  onChangeRoute,
  changeListButtonNode,
  status,
  isMobile,
  isMasterTabsEnabled,
}) => {
  const { tabs, activeTabIndex } = React.useMemo(() => {
    const tabs: TTab[] = [];
    let activeTabIndex = 0;

    Object.entries(MenuItems).forEach(([key, value], index) => {
      if (key === currentRoute) {
        activeTabIndex = index;
      }
      tabs.push({
        text: value,
        disabled: false,
        counterType: 'default',
      } as unknown as TTab);
    });

    return { tabs, activeTabIndex };
  }, [currentRoute]);

  const headerView = React.useMemo(() => {
    if (isMasterTabsEnabled && !isMobile) {
      return null;
    }
    switch (status) {
      case ETopMenuStatus.Offline:
        return (
          <span className={styles['header']}>
            {isMobile ? (
              <UIHeading3>
                <span className={styles['title']}>
                  Ожидание сети <Spinner size={16} />
                </span>
              </UIHeading3>
            ) : (
              <UIHeading4>
                <span className={styles['title']}>
                  Ожидание сети <Spinner size={16} />
                </span>
              </UIHeading4>
            )}
          </span>
        );
      case ETopMenuStatus.ChangeList:
      case ETopMenuStatus.NotAuthorized:
      case ETopMenuStatus.Loading:
      default:
        return (
          <span className={styles['header']}>
            {isMobile ? (
              <UIHeading3>
                <span className={styles['title']}>
                  {DEFAULT_TITLE} {status === ETopMenuStatus.Loading && <Spinner size={16} />}
                </span>
              </UIHeading3>
            ) : (
              <UIHeading4>
                <span className={styles['title']}>
                  {DEFAULT_TITLE} {status === ETopMenuStatus.Loading && <Spinner size={16} />}
                </span>
              </UIHeading4>
            )}
            {changeListButtonNode}
          </span>
        );
    }
  }, [isMasterTabsEnabled, status, isMobile, changeListButtonNode]);

  const tabsView = React.useMemo(() => {
    if (status === ETopMenuStatus.Offline || status === ETopMenuStatus.NotAuthorized) {
      return null;
    }

    return (
      <div className={styles['tabs']}>
        <Tabs
          items={tabs}
          size={isMobile ? 'M' : 'S'}
          activeTabIndex={activeTabIndex}
          onTabClick={(index: number) => {
            onChangeRoute(Object.keys(MenuItems)[index] as TRoute);
          }}
        />
      </div>
    );
  }, [status, tabs, isMobile, activeTabIndex, onChangeRoute]);

  return (
    <div data-testid="TabFolderMenuContainer" className={isMobile ? styles['container-mobile'] : styles['container']}>
      {headerView}
      {tabsView}
    </div>
  );
};
